/**
 * Scheduled tasks that run inside one Work chat (InferenceTask.workSession).
 *
 * Backs the Tasks section of the Sources panel: the same tasks the /tasks page
 * manages, filtered to this thread, plus the create/edit/toggle/delete
 * actions wired through the shared CreateTaskModal so the two surfaces never
 * diverge.
 */
import { useCallback, useMemo, useState } from 'react';
import { useFocusEffect } from 'expo-router';
import useAppActive from '@/hooks/useAppActive';
import { useInferenceTasksStore, useModalStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';

const EMPTY = [];

export default function useWorkChatTasks({ workSessionId, conversationId, chatTitle, enabled = true }) {
  const { t } = useTranslation();
  const { openModal } = useModalStore((state) => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const { addToast } = useToastStore((state) => state.actions);
  const tasksById = useInferenceTasksStore((state) => state.tasks);
  const fetchWorkSessionTasks = useInferenceTasksStore((state) => state.fetchWorkSessionTasks);
  const toggleTask = useInferenceTasksStore((state) => state.toggleTask);
  const deleteTask = useInferenceTasksStore((state) => state.deleteTask);
  const isUpdating = useInferenceTasksStore((state) => state.isLoading.update);

  const isAppActive = useAppActive();
  const active = enabled && !!workSessionId;

  const tasks = useMemo(() => {
    if (!active) return EMPTY;
    const wanted = String(workSessionId);
    return Object.values(tasksById)
      .filter((task) => task && String(task.workSessionId || '') === wanted)
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [active, tasksById, workSessionId]);

  const [loadState, setLoadState] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

  useFocusEffect(useCallback(() => {
    if (!active || !isAppActive) return;
    let cancelled = false;
    setLoadState({ workSessionId, refreshKey, loading: true, error: null });
    fetchWorkSessionTasks(workSessionId).then(() => {
      if (!cancelled) setLoadState({ workSessionId, refreshKey, loading: false, error: null });
    }).catch((error) => {
      if (cancelled) return;
      console.warn('[useWorkChatTasks] fetch failed', error?.message || error);
      setLoadState({ workSessionId, refreshKey, loading: false, error });
      addToast(t('pages.tasks.loadFailed'), 'error');
    });
    return () => { cancelled = true; };
  }, [active, addToast, fetchWorkSessionTasks, isAppActive, refreshKey, t, workSessionId]));

  const confirmDelete = useCallback((taskId) => {
    showConfirmationModal({
      title: t('pages.tasks.deleteTitle'),
      message: t('pages.tasks.deleteMessage'),
      confirmText: t('common.delete'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          await deleteTask(taskId);
          addToast(t('pages.tasks.deleteSuccess'), 'success');
        } catch (error) {
          addToast(t('pages.tasks.deleteFailed'), 'error');
        }
      },
    });
  }, [addToast, deleteTask, showConfirmationModal, t]);

  const onModalResult = useCallback((result) => {
    if (result?.action === 'delete' && result?.taskId) confirmDelete(result.taskId);
  }, [confirmDelete]);

  const addTask = useCallback(() => {
    if (!active) return;
    openModal('CreateTaskModal', {
      workSessionId,
      workChatTitle: chatTitle,
      conversationId,
      onSuccess: onModalResult,
    });
  }, [active, chatTitle, conversationId, onModalResult, openModal, workSessionId]);

  const editTask = useCallback((task) => {
    if (!active || !task?.id) return;
    openModal('CreateTaskModal', {
      task,
      workSessionId,
      workChatTitle: chatTitle,
      onSuccess: onModalResult,
    });
  }, [active, chatTitle, onModalResult, openModal, workSessionId]);

  const setTaskEnabled = useCallback(async (task, isEnabled) => {
    if (!active || !task?.id || isUpdating) return;
    try {
      await toggleTask(task.id, isEnabled);
    } catch (error) {
      addToast(error?.message || t('tasks.failedToSave'), 'error');
    }
  }, [active, addToast, isUpdating, t, toggleTask]);

  const removeTask = useCallback((task) => {
    if (active && task?.id) confirmDelete(task.id);
  }, [active, confirmDelete]);

  const isLoading = active && (loadState?.workSessionId !== workSessionId || loadState.refreshKey !== refreshKey || loadState.loading);
  const loadError = active && loadState?.workSessionId === workSessionId ? loadState.error : null;

  return { tasks, canAddTask: active, isUpdating, isLoading, loadError, refresh, addTask, editTask, setTaskEnabled, removeTask };
}
